// @flow

import React, { type Node } from 'react';
import { Helmet } from 'react-helmet';
import faviconSrc from '@tectonics/favicon.svg';
import { getLookAndFeel, getName } from '@tectonics/prs/merchant';
import { SetInitialPaymentMethod } from '@tectonics/components';
import { type LookAndFeelRootPropsOpenedPayment } from '@tectonics/look-and-feel';
import { AppValuesProvider } from '@tectonics/app-values';
import { PaymentSpecProvider } from '@tectonics/prs/payment-spec';
import { UserInputValuesProvider } from '@tectonics/prs/user-input-values';
import {
  IncomingPaymentProvider,
  useIncomingPayment,
  isRetry,
  getMerchant,
  getCountry,
} from '@tectonics/prs/incoming-payment';
import { SubmittingProvider } from '@tectonics/prs/submitting';
import { ValidationProvider } from '@tectonics/prs/validation';
import { FormStatusContextProvider } from '@tectonics/prs/form-status';
import { CustomReceiptsHandlerProvider } from '@tectonics/prs/custom-receipts';

import lookAndFeel from '../..';
import { GlobalStyle } from '../../commons/global-style';
import { LayoutBase, LayoutSideLeft, LayoutSideRight, MainPanel, MainPanelContent } from '../../template';
import { SideDisplay } from '../../organisms/side-display';
import { MobileDisplayValues } from '../../organisms/mobile-display-values';
import { Header } from '../../organisms/header';
import { Form } from './form';
import { SetFingerprintValue } from '@tectonics/components/set-fingerprint-value';
import { SelectLang } from '@tectonics/components/select-lang';
import { useLanguage } from '@tectonics/controller/language/LanguageContext';
import { translate } from '@tectonics/translate';

export function PageOpenedPayment({
  spec,
  selectedPaymentMethod,
  userInputValues,
  incomingPayment,
  submitting,
  onSelectPaymentMethod,
  appValues,
  setAppValues,
  onUserInput,
  onSubmit,
  userInputValidationErrors,
  submitError,
  customReceiptsHandler,
}: LookAndFeelRootPropsOpenedPayment): Node {
  const merchant = getMerchant(incomingPayment);
  const lookAndFeelName = getLookAndFeel(merchant);
  const title = translate(`${lookAndFeelName}.paymentPageTitle`) ?? 'Página de pagamento';

  return (
    <PaymentSpecProvider spec={spec}>
      <IncomingPaymentProvider incomingPayment={incomingPayment}>
        <AppValuesProvider appValues={appValues} setAppValues={setAppValues}>
          <UserInputValuesProvider values={userInputValues} setValue={onUserInput}>
            <CustomReceiptsHandlerProvider value={customReceiptsHandler}>

              <SetInitialPaymentMethod lookAndFeel={lookAndFeel}/>
              <SetFingerprintValue />
              <ValidationProvider
                userInputValidationErrors={userInputValidationErrors}
                submitError={submitError}
              >
                <SubmittingProvider submitting={submitting}>
                  <FormStatusContextProvider>
                    <Helmet
                      title={`${title}${merchant && ` - ${getName(merchant)}`}`}
                      link={[{ rel: 'icon', href: faviconSrc }]}
                    />

                    <GlobalStyle />
                    <Layout onSubmit={onSubmit} />
                  </FormStatusContextProvider>
                </SubmittingProvider>
              </ValidationProvider>
            </CustomReceiptsHandlerProvider>
          </UserInputValuesProvider>
        </AppValuesProvider>
      </IncomingPaymentProvider>
    </PaymentSpecProvider>
  );
}

type LayoutProps = {|
  onSubmit: (bool, bool) => Promise<bool>,
|};

type SelectEvent = SyntheticInputEvent<HTMLSelectElement>;

function Layout({ onSubmit }: LayoutProps): Node {
  const incomingPayment = useIncomingPayment();
  const retry = isRetry(incomingPayment);
  const country = getCountry(incomingPayment);
  const { currentLanguage, changeLanguage } = useLanguage();

  const handleLangChange = async (event: SelectEvent): Promise<void> => {
    await changeLanguage(event.currentTarget.value);
  };

  return (
    <LayoutBase data-test-id="page-opened-payment">
      <LayoutSideLeft>
        <MainPanel>
          {!retry && <Header />}
          <MobileDisplayValues />
          <MainPanelContent>
            <Form onSubmit={onSubmit} />
          </MainPanelContent>
        </MainPanel>
      </LayoutSideLeft>

      <LayoutSideRight>
        <SelectLang country={country} value={currentLanguage} onChange={handleLangChange} />
        <SideDisplay />
      </LayoutSideRight>
    </LayoutBase>
  );
}
